import { LeadDetailBody as SharedLeadDetailBody } from '@viq-admin/screens';
import { space } from '@viq-admin/ui';
import { useRoleClaim } from '../../auth/useRoleClaim';
import { useCompanyFeatures } from '../../hooks/useCompanyFeatures';
import { cardImagesApi } from '../../api/cardImages';
import { leadDetailApi } from './leadDetailApi';

/**
 * Customer lead detail body — renders the SHARED, parameterized Lead Detail
 * component (HeaderCard + AiSummary + Opportunities + Tasks + Discovery +
 * archive/restore/edit), wired to the customer plane's `/v1/admin/*` endpoints
 * via `leadDetailApi`. Same `{id}` signature as before, so the /leads route and
 * the Live-feed / Batch drawers consume it unchanged. View-level gating stays
 * at the route (RoleGuard) / container level.
 *
 * Edit is additionally gated HERE, to `company_admin` only (stakeholders stay
 * read-only). Defense-in-depth mirrors the console's readOnly treatment of
 * archive/restore (see apps/console's LeadDetailBody): the `updateLead`,
 * `updateNote`, `createNote`, `updateDiscoveryAnswer` and `deleteDiscoveryAnswer` CLOSURES
 * themselves are withheld from the shared component's `api` for any other
 * role, so a bug in the shared component's own edit-affordance gating still
 * can't reach a working save path. Every other method — including
 * `archiveLead`/`restoreLead`, which stay available to every role by design —
 * is preserved via the spread; only these five are omitted.
 *
 * Card images (`photos`) are ADDED here, only for a `company_admin` whose
 * company has card images on (`companies.store_card_images`, set in the
 * console). Without `photos` the shared Card images panel renders nothing.
 * UX only: GET /v1/admin/rawphotos/:id/card-image enforces the same role and
 * flag. The features probe fails closed, so the panel stays hidden until it
 * answers.
 */
export function LeadDetailBody({ id }: { id: string }) {
  const { role } = useRoleClaim();
  const { cardImages } = useCompanyFeatures();
  const isCompanyAdmin = role === 'company_admin';
  const base =
    isCompanyAdmin
      ? leadDetailApi
      : {
          ...leadDetailApi,
          updateLead: undefined,
          updateNote: undefined,
          createNote: undefined,
          updateDiscoveryAnswer: undefined,
          deleteDiscoveryAnswer: undefined,
        };
  const api = isCompanyAdmin && cardImages ? { ...base, photos: cardImagesApi } : base;
  return (
    <div style={{ padding: space.lg }}>
      <SharedLeadDetailBody id={id} api={api} />
    </div>
  );
}
